import { z } from 'zod'
import { pickPublicKey } from '@kairos/supabase/public-key'

/**
 * Zod-validated environment for the admin-dashboard SPA.
 *
 * Reads `import.meta.env` (Vite). Accepts both `VITE_*` and `NEXT_PUBLIC_*`
 * prefixes so the monorepo-root `.env.local` (which uses Next.js naming
 * for the dashboard) just works without duplication.
 *
 * Validation runs at module load — a misconfigured env throws immediately
 * with a precise error message instead of failing later in a network call.
 *
 * Dev defaults: `VITE_ADMIN_API_URL` falls back to `http://localhost:8787`
 * (the gateway's `bun run dev` port in `kairos-connect-edge`). Prod must
 * set it explicitly via CI — the build script in `cd.yml` points it at
 * `https://gateway.thekairos.app`.
 */

type RawEnv = Readonly<Record<string, string | boolean | undefined>>
const raw = import.meta.env as unknown as RawEnv

/** Gateway dev port. The gateway dispatches `/api/v1/admin/*` to admin-api. */
const DEV_GATEWAY_URL = 'http://localhost:8787'
const GATEWAY_HOST = 'gateway.thekairos.app'
const LEGACY_API_HOST = ['api', 'thekairos', 'app'].join('.')

function pick(...keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = raw[key]
    if (typeof value === 'string' && value.length > 0) {
      return value
    }
  }
  return undefined
}

function normalizeGatewayUrl(value: string | undefined): string | undefined {
  if (value === undefined || value === '') return undefined
  try {
    const parsed = new URL(value)
    if (parsed.hostname === LEGACY_API_HOST) {
      parsed.hostname = GATEWAY_HOST
      return parsed.toString().replace(/\/$/, '')
    }
    return value.replace(/\/$/, '')
  } catch {
    return value
  }
}

const envSchema = z.object({
  VITE_SUPABASE_URL: z.string().url(),
  VITE_SUPABASE_ANON_KEY: z
    .string()
    .min(1, 'A Supabase publishable or anon key is required'),
  VITE_ADMIN_API_URL: z.string().url(),
  VITE_SENTRY_DSN: z.string().optional().default(''),
  VITE_SENTRY_RELEASE: z.string().optional().default(''),
})

export type Env = z.infer<typeof envSchema>

function parseEnv(): Env {
  // Resolve admin-api URL with this precedence:
  //   1. Explicit `VITE_ADMIN_API_URL` / `NEXT_PUBLIC_ADMIN_API_URL`.
  //   2. The shared `NEXT_PUBLIC_BFF_URL` the rest of the kairos apps
  //      already consume — both dev and prod use the same gateway.
  //   3. `http://localhost:8787` only as a final dev fallback when no env
  //      is set at all.
  // To point dev at a local gateway instead of prod, set
  // `VITE_ADMIN_API_URL=http://localhost:8787` in `.env.local`.
  const adminApiUrl =
    normalizeGatewayUrl(pick('VITE_ADMIN_API_URL', 'NEXT_PUBLIC_ADMIN_API_URL')) ??
    normalizeGatewayUrl(pick('VITE_BFF_URL', 'NEXT_PUBLIC_BFF_URL')) ??
    (import.meta.env.DEV ? DEV_GATEWAY_URL : undefined)

  const input = {
    VITE_SUPABASE_URL: pick('VITE_SUPABASE_URL', 'NEXT_PUBLIC_SUPABASE_URL'),
    VITE_SUPABASE_ANON_KEY: pickPublicKey(
      [raw['VITE_SUPABASE_PUBLISHABLE_KEY'], raw['NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY']],
      [raw['VITE_SUPABASE_ANON_KEY'], raw['NEXT_PUBLIC_SUPABASE_ANON_KEY']],
    ),
    VITE_ADMIN_API_URL: adminApiUrl,
    VITE_SENTRY_DSN: pick('VITE_SENTRY_DSN', 'NEXT_PUBLIC_SENTRY_DSN'),
    VITE_SENTRY_RELEASE: pick(
      'VITE_SENTRY_RELEASE',
      'NEXT_PUBLIC_SENTRY_RELEASE',
    ),
  }

  const result = envSchema.safeParse(input)

  if (!result.success) {
    const formatted = result.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n')
    throw new Error(
      `Invalid admin-dashboard environment. Check your .env file against .env.example:\n${formatted}`,
    )
  }

  return result.data
}

export const env: Env = parseEnv()

export const isDev: boolean = import.meta.env.DEV
export const isProd: boolean = import.meta.env.PROD
